import { CloudinaryResponse, QuoteCardData } from '../types';

const CLOUDINARY_UPLOAD_PRESET = 'quotes_unsigned';
const CLOUDINARY_CLOUD_NAME = 'llmcd';
const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export class CloudinaryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CloudinaryError';
  }
}

export function validateImage(file: File): void {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    throw new CloudinaryError('Invalid file type. Please upload a JPG, PNG, or WebP image.');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new CloudinaryError('File size exceeds 5MB limit.');
  }
}

export async function uploadToCloudinary(
  file: File,
  quoteData: QuoteCardData
): Promise<{ cloudinary: CloudinaryResponse; quote: QuoteCardData }> {
  try {
    validateImage(file);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', 'quotes');
    
    // Add quote metadata as context
    formData.append('context', JSON.stringify({
      quote_id: quoteData.quoteId,
      author: quoteData.author,
      style: quoteData.cardStyle,
      user_email: quoteData.userEmail
    }));

    const response = await fetch(CLOUDINARY_API_URL, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new CloudinaryError(error.message || 'Failed to upload image');
    }

    const data = await response.json();

    return {
      cloudinary: {
        publicId: data.public_id,
        url: data.secure_url,
        format: data.format,
        width: data.width,
        height: data.height,
        bytes: data.bytes,
        thumbnailUrl: data.secure_url.replace('/upload/', '/upload/c_thumb,w_200,g_face/'),
        originalFilename: data.original_filename
      },
      quote: quoteData
    };
  } catch (error) {
    if (error instanceof CloudinaryError) {
      throw error;
    }
    throw new CloudinaryError('An unexpected error occurred during upload');
  }
}

export function getImageUrl(publicId: string, options: {
  width?: number;
  height?: number;
  crop?: string;
  quality?: number;
} = {}): string {
  const transformations = [];
  
  if (options.width) transformations.push(`w_${options.width}`);
  if (options.height) transformations.push(`h_${options.height}`);
  if (options.crop) transformations.push(`c_${options.crop}`);
  if (options.quality) transformations.push(`q_${options.quality}`);

  const transformationString = transformations.length > 0 
    ? `/upload/${transformations.join(',')}` 
    : '/upload';

  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}${transformationString}/${publicId}`;
}